/**
 * 給与計算参照先カタログ（設定 > 給与計算参照先カタログ）の mock シード。
 *
 * 給与計算が「手元にある確定した情報」を読み解く先を 10 件並べる。読み先は 2 系統:
 * - コア DB の抽出テーブル 3 件。DRESS CODE のコア DB に 1 対 1 で対応させている
 *   （メンバー / 勤怠 / 雇用契約）。行 = worker。
 * - 社内マスタ 7 件（`coreSource: 'in-house'`）。顧客の賃金規程・経費規程・賞与規程が定める
 *   表で、行 = 照会キー（資格・等級 × 号俸・役職・扶養区分・社員 × 対象年月・査定ランク・
 *   等級 × 査定ランク）。
 *
 * 法定基準一覧（`statutoryStandards.ts`）との違いはカタログの性格そのもの:
 * - 法定基準は手元に無い値（料額表・税額表）を法令の表に求めて合致させる先なので、
 *   出典 URL（`sources`）を持つ。
 * - 参照先は手元にある確定した情報を引き比べる先なので、出典 URL を持たない。出典は
 *   社外の法令ではなく DRESS CODE 自身のコア DB か顧客の規程であり、それは `coreSource` と
 *   各列の `sourcePath` が表す。
 *
 * コア DB 抽出 3 表の列の一次ソースは payroll item カタログ（`payrollItemCatalog.ts`）の
 * `category: 'reference'`。
 * 各項目の説明が明示する供給元（従業員マスタ / 申告データ / 勤怠 / 雇用契約）でどの表に
 * 属するかを割り振り、列は `payrollItemId` でカタログを名指しする。payroll item に無いが
 * 給与計算に要る細目は、給与計算入力スナップショット（`payrollInputSnapshots.ts`）由来の
 * 項目を各表の末尾に足して補っている。
 *
 * 参照先 1 件も `valueType: 'table'` の payroll item と 1 対 1 で対応する
 * （R048 メンバー / R049 勤怠 / R051 契約 / 社内マスタは I001〜I004・I015・B002・B070）。表そのものが
 * 給与計算から見れば 1 個の参照項目なので、列と同じく `code` ではなく `id` で名指しする。
 * 社内マスタが `I` / `B` 始まりの code を持つのは、項目カタログ側の category が `inquiry` の
 * ままだから。項目の分類は「値を照会キーで引く表かどうか」の軸で、参照先カタログに
 * 載るかどうかとは別軸なので寄せていない。
 *
 * コア DB 抽出 3 表の行は worker。3 表とも同じ 10 名・同じ並びで、値は表をまたいで
 * 整合させてある（所定労働時間（1日）× 出勤日数 − 遅刻早退時間 = 所定内労働時間、
 * 報酬額 → 標準報酬月額 など）。氏名・社員番号は法定基準一覧の「労働者別 住民税額」と
 * 同じ架空の従業員を使っている。社内マスタ 5 表のうち月次経費精算だけが同じ 10 名を持ち、
 * 残る 4 表は worker を持たない。
 *
 * 有効期間はコア DB 抽出が入力スナップショットの運用開始日（2026-01-01）に合わせた無期限、
 * 社内マスタは規程の適用開始日で、基準日 2026-08-11 で全件が期間内になる。
 * 一覧は `sortOrder` 昇順（メンバー → 勤怠 → 契約 → 社内マスタ）。
 */

import type { DemoDatabase } from '../schema';
import type { PayrollReferenceCatalogEntry } from '../types';
import { bonusEvaluationCoefficientTable, bonusPointTable, familyAllowanceTable, monthlyExpenseReimbursementTable, payrollReferenceAttendanceTable, payrollReferenceContractTable, payrollReferenceMemberTable, positionAllowanceTable, skillAllowanceTable, wageGradeTable } from './payrollReferenceCatalogTables';

/**
 * 参照先 8 件。`payrollItemId` は `valueType: 'table'` の payroll item を指す。
 * カタログ側の id を差し替えるとこの 8 件が黙って宙に浮くため、対応関係は
 * `payrollReferenceCatalog.test.ts` が固定している（テストから読むので export する）。
 */
export const PAYROLL_REFERENCE_CATALOG_ENTRIES: PayrollReferenceCatalogEntry[] = [{
  id: 'prc-member',
  name: '給与計算用のメンバーテーブル',
  description: 'メンバーで持つ情報項目のうち、給与計算に使うものだけを抽出したテーブル。年齢・扶養親族等の数・税額表区分から、社会保険の被保険者番号・標準報酬月額・住民税課税市区町村までを労働者ごとに引ける。\n値はメンバー DB から給与計算基準日時点で凍結します。基準日より後に生年月日や扶養情報が変わっても、確定済みの給与計算はこのテーブルの値のまま再計算されません。\nこのテーブル自体が給与計算項目の「R048 給与計算参照メンバーテーブル」に対応します。列は供給元が従業員マスタ(People) の R001〜R010 / R014 / R016〜R019 / R041〜R044 と、申告データの R020 / R046 を全て含みます。\n年齢は生年月日から導出した基準日時点の値です。介護保険第2号被保険者区分（40歳以上65歳未満）は年齢から判定するため、生年月日を直したときは年齢と介護保険区分の両方が変わります。\n扶養家族数（R007）と扶養親族等の数（R020）は供給元が別で、値も一致するとは限りません。前者はメンバー DB の実数、後者は扶養控除等申告書に基づく源泉徴収税額表上の数（障害者・寡婦等の加算込み）です。多くの労働者では同数になりますが、加算がある場合だけずれます。\n標準報酬月額は健康保険と厚生年金で別々に持ちます。厚生年金は下限 88,000 円・上限 650,000 円で頭打ちになるため、報酬が高い労働者ほど健康保険側と乖離します。\n報酬月額（R041）は契約テーブルの 基本給 + 役職手当 + 固定残業手当 に定期代（R008）を足した額です。賃金形態が月給でない労働者は基本給の代わりに、時給なら 時間単価 × 勤怠テーブルの所定内労働時間、日給なら 日額単価 × 勤怠テーブルの出勤日数（R025）を足します。契約テーブルの値を直したときはこの列も合わせて直す必要があります。\n住民税課税市区町村は住民票住所から決まるため、住所変更の届出が遅れると特別徴収の納入先がずれます。\nDRESS CODE 上はサンプル値（架空の従業員）です。実データはメンバー DB の登録内容がそのまま出ます。',
  payrollItemId: 'pi-r048',
  coreSource: 'people',
  period: {
    start: '2026-01-01',
    end: null
  },
  table: payrollReferenceMemberTable,
  sortOrder: 1,
  updatedAt: '2026-08-01T09:00:00Z'
}, {
  id: 'prc-attendance',
  name: '給与計算用の勤怠テーブル',
  description: '勤怠で持つ情報項目のうち、給与計算に使うものだけを抽出したテーブル。所定労働日数・出勤日数・欠勤日数から、時間外・深夜・休日の労働時間と休暇取得日数までを労働者ごとに引ける。\n値は勤怠 DB の月次集計から給与計算基準日時点で凍結します。締め後の打刻修正はこのテーブルには反映されず、翌月の遡及精算で扱います。\nこのテーブル自体が給与計算項目の「R049 給与計算参照勤怠テーブル」に対応します。列は供給元が勤怠データ・勤怠集計の R021〜R039 と R050 労働時間数を全て含みます。\n時間は「時間:分」表記です。給与計算項目としては分で持つ値の表示換算であり、小数の時間（1.5 時間）ではないため、割増賃金の計算で 60 進の繰り上がりを取り違えないようにしてください。\n残業時間（R023）は打刻ベースの所定超過時間、時間外労働時間（制度反映・R038）は勤務時間制度を反映した後の法定外時間外です。契約テーブルの勤務時間制度区分が変形労働時間制の労働者だけ、この 2 列が食い違います。\n60時間超時間外労働時間は割増率を 50% に引き上げる判定に使います。法定休日の労働時間はこの算定に含めませんが、法定外休日（所定休日）の労働時間は含めます。\n割増単価算出時間（R030）と欠勤控除算出日数（R031）は当月の実績ではなく就業規則・会社設定が定める分母です。当月所定労働時間（R022）・当月所定労働日数（R039）とは一致しないのが正常です。\n固定残業時間の超過分は、時間外労働時間から雇用契約の固定残業時間を差し引いた差額精算の対象時間です。契約に固定残業の定めが無い労働者は「—」になります。\n管理監督者（労基法 41 条 2 号）に該当する労働者は時間外・休日の割増対象外のため時間外労働時間が 0:00 になりますが、深夜割増は必要なので深夜労働時間は集計します。\nDRESS CODE 上はサンプル値（架空の従業員）です。勤怠 DB は設計中のため、列構成は実装に合わせて変わります。',
  payrollItemId: 'pi-r049',
  coreSource: 'attendances',
  period: {
    start: '2026-01-01',
    end: null
  },
  table: payrollReferenceAttendanceTable,
  sortOrder: 2,
  updatedAt: '2026-08-01T09:00:00Z'
}, {
  id: 'prc-contract',
  name: '給与計算用の契約テーブル',
  description: '契約で持つ情報項目のうち、給与計算に使うものだけを抽出したテーブル。雇用区分・入社日・退職日・勤務時間制度区分から、基本給・賃金形態・各種単価・固定残業手当・所定労働時間・契約期間・支給サイクルまでを労働者ごとに引ける。\n値は雇用契約 DB から給与計算基準日時点で凍結します。期中の契約改定は改定適用日で判定し、遡及分は差額精算で扱います。\nこのテーブル自体が給与計算項目の「R051 給与計算参照契約テーブル」に対応します。列は供給元が雇用契約（労働条件）の R011 雇用区分 / R012 入社日 / R013 退職日 / R015 勤務時間制度区分 を全て含みます。\n「R040 基本給」は給与計算項目としては計算によって得られる固定支給項目であり、雇用契約が定める月額基本給とは別物です。そのためこのテーブルの基本給列には R040 を紐づけていません。\n賃金形態によって意味を持つ単価が変わります。月給は基本給、時給は時間単価、日給は日額単価だけを持ち、使わない単価は「—」になります。\n入社日（R012）は通算の在籍開始日、契約期間は現に効力のある契約の期間です。定年後再雇用のように両者がずれる労働者がいるため、勤続年数の判定に契約期間を使わないでください。\n列のうち「（モック）」が付くものは雇用契約 DB が未実装のため、このテーブル内だけのモック値です。契約ドメインには対応する項目がまだ無く、実装が追いつくまで給与計算が何を読む必要があるかを示すために置いています。\nモック列は契約種別 / 週所定労働日数 / 週所定労働時間 / 試用期間 / 変形労働時間制の区分 / 管理監督者区分 / 36協定の時間外上限（月・年） / 賃金締切日・賃金支払日 の 9 列です。給与計算項目にもコア DB 上の在り処にも対応しないため、この 9 列だけは列から辿れる先がありません。\nDRESS CODE 上はサンプル値（架空の従業員）です。実データは雇用契約 DB の登録内容がそのまま出ます。',
  payrollItemId: 'pi-r051',
  coreSource: 'contracts',
  period: {
    start: '2026-01-01',
    end: null
  },
  table: payrollReferenceContractTable,
  sortOrder: 3,
  updatedAt: '2026-08-01T09:00:00Z'
}, {
  id: 'prc-skill-allowance',
  name: '技術手当テーブル',
  description: '社内で管理している技術手当保持者への手当金額の対応表。DRESS CODEにて、対象の個人資格保有情報は管理しており、資格に対応する手当金額を照会可能。\nこの表は社内（顧客管理）の値のため、DRESS CODE 上はサンプル値です。実データは顧客の規程に合わせて登録します。\n同一人が複数保有する場合の支給ルール（最上位のみ / 合算 / 上限）は規程で定めます。\n対象者は DRESS CODE の個人資格保有情報から判定します。',
  payrollItemId: 'pi-i001',
  coreSource: 'in-house',
  period: {
    start: '2024-04-01',
    end: null
  },
  table: skillAllowanceTable,
  sortOrder: 4,
  updatedAt: '2026-08-01T09:00:00Z'
}, {
  id: 'prc-wage-grade-table',
  name: '号俸表（賃金テーブル）',
  description: '等級 × 号俸に応じた基本給を返す。社内（顧客管理）で管理し、ベア・定昇に伴って年度改定する。\nこの表は社内（顧客管理）の値のため、DRESS CODE 上はサンプル値です。実データは顧客の規程に合わせて登録します。\n等級 × 号俸の 1 組で 1 行を持ちます（等級昇順 → 号俸昇順）。\nベア（ベースアップ）は表全体の金額改定、定昇は号俸の進みで表現します。\n実際の等級数・号俸数は顧客の賃金規程に合わせます。',
  payrollItemId: 'pi-i002',
  coreSource: 'in-house',
  period: {
    start: '2026-04-01',
    end: null
  },
  table: wageGradeTable,
  sortOrder: 5,
  updatedAt: '2026-08-01T09:00:00Z'
}, {
  id: 'prc-position-allowance',
  name: '役職手当 単価表',
  description: '役職に応じた役職手当額を返す。社内（顧客管理）で管理し、規程改定時に更新する。\nこの表は社内（顧客管理）の値のため、DRESS CODE 上はサンプル値です。実データは顧客の規程に合わせて登録します。\n管理監督者（労基法 41 条 2 号）に該当する役職は時間外割増の対象外ですが、深夜割増は必要です。',
  payrollItemId: 'pi-i003',
  coreSource: 'in-house',
  period: {
    start: '2024-04-01',
    end: null
  },
  table: positionAllowanceTable,
  sortOrder: 6,
  updatedAt: '2026-08-01T09:00:00Z'
}, {
  id: 'prc-family-allowance',
  name: '家族手当 単価表',
  description: '扶養区分に応じた家族手当単価を返す。社内（顧客管理）で管理し、規程改定時に更新する。\nこの表は社内（顧客管理）の値のため、DRESS CODE 上はサンプル値です。実データは顧客の規程に合わせて登録します。\n家族手当は所得税の課税対象です（通勤手当と異なり非課税枠はありません）。\n税法上の扶養（源泉控除対象親族）と手当支給要件の扶養は別基準になりうるため、規程で定義を分けます。',
  payrollItemId: 'pi-i004',
  coreSource: 'in-house',
  period: {
    start: '2024-04-01',
    end: null
  },
  table: familyAllowanceTable,
  sortOrder: 7,
  updatedAt: '2026-08-01T09:00:00Z'
}, {
  id: 'prc-monthly-expense-reimbursement',
  name: '月次経費精算',
  description: '社員 × 対象年月に応じた立替経費の精算額を返す。経費精算 SaaS の月次出力を外部取込で管理し、月次締め後に更新する。\n値の一次ソースは社外の経費精算 SaaS が出力した人ごと・月ごとの経費明細です。DRESS CODE 側に一次データは無く、外部取込でしか値を持たないため、この表はサンプル値（架空の従業員）です。\n取込タイミングは経費精算 SaaS 側の月次締め（最終承認の完了）後です。締め後に承認された申請は発生月ではなく翌月の取込に回ります。\n読み先が in-house なのは、法令が定める料額表・税額表ではなく、自社の経費規程が定める精算区分・上限に沿った運用データのためです。住民税額と同じ外部取込でも法定基準一覧には載りません。\n精算合計額 = 交通費 + 出張旅費 + 接待交際費 + 図書研修費 + 備品消耗品費 + その他。課税対象額 + 非課税額 = 精算合計額です。\n実費弁償の立替経費は原則非課税で所得税の課税対象になりません。ただし精算を求めない定額支給（渡切交際費など）は給与として課税対象になるため、「課税対象額」列で分けています。\n「交通費」は業務上の移動にかかった実費で、通勤手当（定期代）は含みません。通勤費の非課税枠は法定基準一覧の「通勤費 非課税限度額表」で別に判定します。\n支給月と経費発生月はずれます。この表の 2026-07 分は 2026 年 8 月支給の給与に載せる想定です。\n承認日は最終承認者の承認日です。未承認・差戻中の申請は SaaS 側の出力に含まれないため、この表に載る額はすべて精算確定済みです。',
  payrollItemId: 'pi-i015',
  coreSource: 'in-house',
  period: {
    start: '2026-01-01',
    end: null
  },
  table: monthlyExpenseReimbursementTable,
  sortOrder: 8,
  updatedAt: '2026-08-01T09:00:00Z'
}, {
  id: 'prc-bonus-evaluation-coefficient',
  name: '査定ランク係数変換表',
  description: '人事評価で確定した査定ランク（S/A/B/C/D）と査定点に応じた賞与評価係数を返す。社内（顧客管理）で管理し、賞与期ごとに見直す。\nこの表は社内（顧客管理）の値のため、DRESS CODE 上はサンプル値です。実データは顧客の賞与規程に合わせて登録します。\n査定ランクと査定点は併用できます。点数型の評価は査定点の下限以上でランクを決めてから係数へ変換します。\n等級・職種ごとに別の変換表を並存させられます。どの表を使うかは給与計算体系のパラメータで決めます。\n係数の値は評価確定日のスナップショットで凍結し、以後の評価修正は遡及処理で扱います。',
  payrollItemId: 'pi-b002',
  coreSource: 'in-house',
  period: {
    start: '2026-04-01',
    end: null
  },
  table: bonusEvaluationCoefficientTable,
  sortOrder: 9,
  updatedAt: '2026-08-18T09:00:00Z'
}, {
  id: 'prc-bonus-point',
  name: '賞与ポイント表',
  description: '等級と査定ランクの組み合わせに対応する賞与ポイントを返す。社内（顧客管理）で管理し、賞与期ごとに見直す。\nこの表は社内（顧客管理）の値のため、DRESS CODE 上はサンプル値です。実データは顧客の賞与規程に合わせて登録します。\n等級 B 評価を 1.0 とした相対値です。ポイント制の賞与では、部門原資配分枠をこの点数の合計で割って 1 点単価を出し、単価 × 各人の点数で配分します。\n上位等級ほど査定による振れ幅を大きく取るのが一般的です（1 等級は 0.8〜1.2、5 等級は 2.8〜5.2）。\n在籍期間の按分と出勤係数は、この表から引いた点数に乗じます。年次有給休暇の取得日は欠勤に含めません。',
  payrollItemId: 'pi-b070',
  coreSource: 'in-house',
  period: {
    start: '2026-04-01',
    end: null
  },
  table: bonusPointTable,
  sortOrder: 10,
  updatedAt: '2026-08-18T10:00:00Z'
}];
export async function seedPayrollReferenceCatalogData(db: DemoDatabase): Promise<void> {
  if ((await db.payrollReferenceCatalogEntries.count()) === 0) {
    await db.payrollReferenceCatalogEntries.bulkAdd(PAYROLL_REFERENCE_CATALOG_ENTRIES);
  }
}